/**
 * @voltxampere/engine — Newton + Anderson-II solve driver
 *
 * Wraps a Newton iteration loop with the {@link AndersonAccelerator}
 * wired in between the LU solve and the convergence check. At each
 * iteration the driver:
 *
 *   1. Builds `Y(x_k)`, `rhs(x_k)` and computes `f_k = Y·x_k - rhs`.
 *   2. LU-solves `Y·xNewton = rhs` to get the plain Newton step.
 *   3. Hands `(x_k, f_k, xNewton, nodeIndexSet)` to the accelerator.
 *      The accelerator returns either the Anderson-accelerated
 *      iterate or the plain Newton step (whichever has smaller
 *      residual).
 *   4. Applies Sprint 8a delta-V damping on top of the accepted
 *      step.
 *   5. Checks the reduced-KCL convergence predicate.
 *
 * Sprint 8b's ladder places this after gmin and source stepping —
 * it is the last-resort fallback for pathological topologies
 * (PNP CE saturation-edge, nested Widlar current mirrors, etc.)
 * that limit-cycle on plain Newton but yield to Pulay mixing.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ConvergenceError, ContractViolationError, SingularMatrixError } from "@voltxampere/shared";
import { sparseSolve, isFiniteVector } from "@voltxampere/math";
import { buildMNA } from "./mna-builder.js";
import { applyDeltaVDamping } from "./delta-v-damping.js";
import { REFERENCE_TEMPERATURE_K, vtAtTemperature, } from "../stamps/nonlinear/physical-constants.js";
import { computeResidual, isConvergedReduced } from "./residual.js";
import { AndersonAccelerator } from "./anderson.js";
/**
 * Solve `f(x) = 0` with Newton + Anderson-II acceleration. Returns
 * the same shape as {@link NewtonRaphsonResult} so the DCSolver can
 * treat this as a drop-in fallback.
 */
export function newtonRaphsonAnderson(circuit, plan, opts) {
    if (!Number.isInteger(opts.maxIterations) || opts.maxIterations <= 0) {
        throw new ContractViolationError(`newtonRaphsonAnderson: maxIterations must be a positive integer, got ${String(opts.maxIterations)}`);
    }
    if (opts.x0.length !== plan.size) {
        throw new ContractViolationError(`newtonRaphsonAnderson: x0 length ${String(opts.x0.length)} ≠ N+M ${String(plan.size)}`);
    }
    if (plan.size === 0) {
        return {
            converged: true,
            iterations: 0,
            x: new Float64Array(0),
            clampEvents: 0,
            finalResidualNorm: 0,
            armijoEvents: 0,
        };
    }
    let x = new Float64Array(opts.x0);
    let iterations = 0;
    let clampEvents = 0;
    let finalResidualNorm = 0;
    let previousStepDelta = null;
    let previousDeltas = null;
    const junctionHistory = new Map();
    const dampingEnabled = opts.enableDeltaVDamping ?? true;
    const tier = opts.tier ?? "strict";
    const accel = new AndersonAccelerator(opts.anderson);
    const temperatureK = opts.temperature ?? REFERENCE_TEMPERATURE_K;
    const deltaVLimit = 10 * vtAtTemperature(temperatureK);
    for (let iter = 1; iter <= opts.maxIterations; iter++) {
        const { Y, rhs } = buildMNA(circuit, plan, {
            xPrev: x,
            gmin: opts.gmin ?? 0,
            sourceScale: opts.sourceScale ?? 1,
            junctionHistory,
            enableDeltaVDamping: dampingEnabled,
            temperature: temperatureK,
        });
        // Top-of-loop convergence check — same pattern as
        // `newtonRaphson`. `Y` and `rhs` are freshly built at `x`, so
        // `residual = Y·x - rhs` evaluates `f(x)`.
        if (previousStepDelta !== null) {
            const residual = computeResidual(Y, x, rhs);
            let residMax = 0;
            for (const i of plan.nodeIndexSet) {
                const r = residual[i] ?? 0;
                const abs = Math.abs(r);
                if (abs > residMax)
                    residMax = abs;
            }
            finalResidualNorm = residMax;
            const converged = isConvergedReduced({
                x,
                residual,
                deltaX: previousStepDelta,
                nodeIndexSet: plan.nodeIndexSet,
                tier,
            });
            if (converged) {
                return {
                    converged: true,
                    iterations: iter - 1,
                    x,
                    clampEvents,
                    finalResidualNorm,
                    armijoEvents: 0,
                };
            }
        }
        // Newton step. If LU throws (e.g. zero-pivot Y matrix on an
        // Anderson-perturbed iterate), reset Anderson's history, back
        // off the iterate to the last accepted step, and try again
        // next iter — a restart usually clears ill-conditioned history.
        let xNewton;
        try {
            xNewton = sparseSolve(Y, rhs);
        }
        catch (e) {
            if (e instanceof SingularMatrixError) {
                // Bail out of Anderson for this solve — the caller (DCSolver)
                // will try the next tier / fallback.
                throw new ConvergenceError(`Newton-Raphson + Anderson hit singular MNA at iter ${String(iter)}`, { meta: { iterations: iter, cause: "singular-lu" }, cause: e });
            }
            throw e;
        }
        iterations = iter;
        if (!isFiniteVector(xNewton)) {
            throw new ConvergenceError(`Newton-Raphson + Anderson produced non-finite iterate at step ${String(iter)}`, { meta: { iterations: iter } });
        }
        // Anderson accelerate: consumes (x_k, f_k, xNewton, indices).
        // f_k was already computed above iff previousStepDelta is set —
        // in the first iter we build a fresh residual at x_0.
        const fk = computeResidual(Y, x, rhs);
        const accelResult = accel.step(x, fk, xNewton, plan.nodeIndexSet);
        let xNewAccepted = accelResult.xNext;
        if (!isFiniteVector(xNewAccepted)) {
            throw new ConvergenceError(`Anderson accelerator produced non-finite iterate at step ${String(iter)}`, { meta: { iterations: iter } });
        }
        // Sprint 8b.1 Fix #2: caller-side residual verification. Build
        // f(xAccel) via a fresh MNA pass and compare against ||f_k||
        // on the node-KCL projection. If accelerated step grew the
        // residual, revert the Anderson history sample (so future
        // steps are not built on a bad iterate) and fall back to the
        // plain Newton step.
        if (accelResult.accelerated) {
            let fAccelMaxNode = 0;
            try {
                const { Y: Yacc, rhs: rhsAcc } = buildMNA(circuit, plan, {
                    xPrev: xNewAccepted,
                    gmin: opts.gmin ?? 0,
                    sourceScale: opts.sourceScale ?? 1,
                    junctionHistory: new Map(junctionHistory), // sandbox; do not poison
                    enableDeltaVDamping: dampingEnabled,
                    temperature: temperatureK,
                });
                const fAccel = computeResidual(Yacc, xNewAccepted, rhsAcc);
                for (const i of plan.nodeIndexSet) {
                    const r = Math.abs(fAccel[i] ?? 0);
                    if (r > fAccelMaxNode)
                        fAccelMaxNode = r;
                }
            }
            catch {
                // If MNA build for the accelerated iterate throws, treat
                // as failed verification — don't accept the proposal.
                fAccelMaxNode = Number.POSITIVE_INFINITY;
            }
            let fkMaxNode = 0;
            for (const i of plan.nodeIndexSet) {
                const r = Math.abs(fk[i] ?? 0);
                if (r > fkMaxNode)
                    fkMaxNode = r;
            }
            if (fAccelMaxNode >= fkMaxNode) {
                // Anderson made things worse. Revert history, take Newton.
                accel.revertLast();
                xNewAccepted = new Float64Array(xNewton);
            }
        }
        // Sprint 8a delta-V damping on the accepted step (Anderson or
        // plain Newton, whichever we took).
        let xNew;
        if (dampingEnabled && plan.junctionBearingIndices.size > 0) {
            const damped = applyDeltaVDamping(xNewAccepted, x, plan.junctionBearingIndices, deltaVLimit, previousDeltas);
            xNew = damped.result;
            if (damped.alpha < 1)
                clampEvents += 1;
            previousDeltas = damped.junctionDeltas;
        }
        else {
            xNew = xNewAccepted;
        }
        const stepDelta = new Float64Array(xNew.length);
        for (let i = 0; i < xNew.length; i++)
            stepDelta[i] = (xNew[i] ?? 0) - (x[i] ?? 0);
        previousStepDelta = stepDelta;
        x = xNew;
    }
    // Final check after max-iter.
    if (previousStepDelta !== null) {
        const { Y: Yf, rhs: rhsf } = buildMNA(circuit, plan, {
            xPrev: x,
            gmin: opts.gmin ?? 0,
            sourceScale: opts.sourceScale ?? 1,
            junctionHistory,
            enableDeltaVDamping: dampingEnabled,
            temperature: temperatureK,
        });
        const residual = computeResidual(Yf, x, rhsf);
        let residMax = 0;
        for (const i of plan.nodeIndexSet) {
            const r = residual[i] ?? 0;
            const abs = Math.abs(r);
            if (abs > residMax)
                residMax = abs;
        }
        finalResidualNorm = residMax;
        const converged = isConvergedReduced({
            x,
            residual,
            deltaX: previousStepDelta,
            nodeIndexSet: plan.nodeIndexSet,
            tier,
        });
        if (converged) {
            return {
                converged: true,
                iterations,
                x,
                clampEvents,
                finalResidualNorm,
                armijoEvents: 0,
            };
        }
    }
    return {
        converged: false,
        iterations,
        x,
        clampEvents,
        finalResidualNorm,
        armijoEvents: 0,
    };
}
//# sourceMappingURL=anderson-newton.js.map